import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Toaster } from 'sonner'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import HomeRedirect from './components/HomeRedirect'
import { useAuth } from './hooks/useAuth'
import HomePage from './pages/HomePage'
import LoginRedirect from './pages/LoginRedirect'
import MagicLoginPage from './pages/MagicLoginPage'
// OnboardingPage removed (#691) - OnboardingModal is the single onboarding flow
import ClubsPage from './pages/ClubsPage'
import ActivityPage from './pages/ActivityPage'
import ProfilePage from './pages/ProfilePage'
import ClubDetailPage from './pages/ClubDetailPage'
import CreateClubPage from './pages/CreateClubPage'
import ClubMembersPage from './pages/ClubMembersPage'
// ClubSettingsPage removed (#901) - replaced by sub-pages
import ClubSettingsHubPage from './pages/settings/ClubSettingsHubPage'
import ClubSettingsGeneralPage from './pages/settings/ClubSettingsGeneralPage'
import ClubSettingsPrivacyPage from './pages/settings/ClubSettingsPrivacyPage'
// import ClubSettingsMembersPage from './pages/settings/ClubSettingsMembersPage' // Legacy - replaced by redirect (#1227)
import ClubSettingsMembersRedirect from './pages/settings/ClubSettingsMembersRedirect'
import ClubSettingsInvitesPage from './pages/settings/ClubSettingsInvitesPage'
import ClubSettingsDangerPage from './pages/settings/ClubSettingsDangerPage'
import ClubSettingsCalendarPage from './pages/settings/ClubSettingsCalendarPage'
import ClubSettingsEventsPage from './pages/settings/ClubSettingsEventsPage'
import ClubSettingsMembershipRequestsPage from './pages/settings/ClubSettingsMembershipRequestsPage'
// import ClubEditPage from './pages/ClubEditPage' // Legacy - replaced by ClubEditRedirect
import ClubEventsPage from './pages/ClubEventsPage'
import ClubEventsManagePage from './pages/ClubEventsManagePage'
import EventSeriesDetailPage from './pages/EventSeriesDetailPage'
import EventSeriesEditPage from './pages/EventSeriesEditPage'
// Lazy-loaded pages - heavy libs (MapLibre, QR scanner)
const EventDetailPage = lazy(() => import('./pages/EventDetailPage'))
const EventCreatePage = lazy(() => import('./pages/EventCreatePage'))
const GlobalEventCreatePage = lazy(() => import('./pages/GlobalEventCreatePage'))
const EventEditPage = lazy(() => import('./pages/EventEditPage'))
import EventCheckInPage from './pages/EventCheckInPage'
const EventAdminCheckInPage = lazy(() => import('./pages/EventAdminCheckInPage'))
const QRScannerPage = lazy(() => import('./pages/QRScannerPage'))
import PublicProfilePage from './pages/PublicProfilePage'
import ClubChatPage from './pages/ClubChatPage'
import ChatLandingPage from './pages/ChatLandingPage'
// #2846: /playground is a dev-only design-system showcase — gated to non-prod
// builds and dead-code-eliminated from the production bundle. `uiPlaygroundRoute`
// is `null` in a production build (so the <Route> below is never registered and
// /playground 404s) and a lazy UiPlayground everywhere else. See that module for
// the MODE-gating rationale + DCE evidence.
import { uiPlaygroundRoute as UiPlayground } from './pages/uiPlaygroundRoute'
import InviteAcceptPage from './pages/InviteAcceptPage'
import WaitlistPage from './pages/WaitlistPage'
import PrivacyPage from './pages/PrivacyPage'
import TermsPage from './pages/TermsPage'
import SmsHelpPage from './pages/SmsHelpPage'
import LeaderGettingStartedPage from './pages/LeaderGettingStartedPage'
import LeaderWhyPage from './pages/LeaderWhyPage'
import MembersPage from './pages/MembersPage'
import AppPage from './pages/AppPage'
import AboutPage from './pages/AboutPage'
import FaqPage from './pages/FaqPage'
import ContactPage from './pages/ContactPage'
import UnsubscribedPage from './pages/UnsubscribedPage'
import UnsubscribeErrorPage from './pages/UnsubscribeErrorPage'
import AppHomePage from './pages/AppHomePage'
// Lazy-loaded admin pages - rarely visited
const AdminInvitesPage = lazy(() => import('./pages/AdminInvitesPage'))
const AdminReportsPage = lazy(() => import('./pages/AdminReportsPage'))
const AdminFeedbackPage = lazy(() => import('./pages/AdminFeedbackPage'))
const AdminContactMessagesPage = lazy(() => import('./pages/AdminContactMessagesPage'))
// Training Group pages hidden for v1.2 release (#1241) - re-enable in v1.2.1
// import TrainingGroupCreatePage from './pages/TrainingGroupCreatePage'
// import TrainingGroupDetailPage from './pages/TrainingGroupDetailPage'
// import TrainingGroupEditPage from './pages/TrainingGroupEditPage'
// import TrainingGroupEventCreatePage from './pages/TrainingGroupEventCreatePage'
// Lazy-loaded platform admin pages - rarely visited
const PlatformAdminHomePage = lazy(() => import('./pages/PlatformAdminHomePage'))
const PlatformAdminClubsPage = lazy(() => import('./pages/PlatformAdminClubsPage'))
const PlatformAdminClubDetailPage = lazy(() => import('./pages/PlatformAdminClubDetailPage'))
const PlatformAdminMembersPage = lazy(() => import('./pages/PlatformAdminMembersPage'))
const PlatformAdminSubscriptionsPage = lazy(() => import('./pages/PlatformAdminSubscriptionsPage'))
import NotFoundPage from './pages/NotFoundPage'
import LoadingPage from './pages/LoadingPage'
// import ProfileEditPage from './pages/ProfileEditPage' // Legacy - replaced by ProfileSettingsPage
import SettingsPage from './pages/SettingsPage'
import { meNotificationsRedirectRoute, memberIdRedirectRoute } from './routes/legacyRedirects'
// SettingsHubPage deleted (#756) - replaced by SettingsPage (#738)
import ProfileSettingsPage from './pages/settings/ProfileSettingsPage'
import EmailChangePage from './pages/settings/EmailChangePage'
import EmailChangeConfirmPage from './pages/settings/EmailChangeConfirmPage'
import AccountManagementPage from './pages/settings/AccountManagementPage'
import DeleteAccountConfirmPage from './pages/settings/DeleteAccountConfirmPage'
import PreferencesSettingsPage from './pages/settings/PreferencesSettingsPage'
import AccessibilitySettingsPage from './pages/settings/AccessibilitySettingsPage'
import NotificationSettingsPage from './pages/settings/NotificationSettingsPage'
import CalendarFeedPage from './pages/settings/CalendarFeedPage'
import BlockedUsersPage from './pages/settings/BlockedUsersPage'
import ReleaseNotesPage from './pages/ReleaseNotesPage'
import FeedbackPage from './pages/FeedbackPage'
import HelpIndexPage from './pages/HelpIndexPage'
import HelpCategoryPage from './pages/HelpCategoryPage'
import HelpArticlePage from './pages/HelpArticlePage'
import ClubEditRedirect from './pages/ClubEditRedirect'
import ScrollToTop from './components/ScrollToTop'
import OfflineSyncStatus from './components/OfflineSyncStatus'
import { PWAManager } from './components/PWAManager' // Smart install prompt (Issue #209)
import { AuthModalProvider, useAuthModal } from './contexts/AuthModalContext'
import LoginModal from './components/LoginModal'
import OnboardingModal from './components/onboarding/OnboardingModal'
import { TourProvider } from './components/tour/TourProvider'
import { useTour } from './components/tour/useTour'
import { TourPrompt } from './components/tour/TourPrompt'

function AppContent() {
  const { isModalOpen, pendingNavigation, closeLoginModal } = useAuthModal();
  const { user } = useAuth();
  const location = useLocation();

  // Determine if we should show onboarding modal (Issue #563)
  // Show for ANY new user, regardless of invite context
  const shouldShowOnboarding =
    user &&
    !user.onboarding_completed;

  // Determine if we should show the tour prompt (#1559)
  // Onboarding finished, tour not yet completed/dismissed.
  // #1585: Suppressed on /invite/* so the prompt does not cover the invite-accept
  // page or race the auto-accept mutation. After auto-accept navigates to
  // /clubs/{slug}, this gate re-evaluates and the prompt appears at the right moment.
  const shouldShowTourPrompt =
    user &&
    user.onboarding_completed &&
    user.tour_completed_at === null &&
    !location.pathname.startsWith('/invite/');

  return (
    <TourProvider>
      <ScrollToTop />
      <OfflineSyncStatus />
      <PWAManager />
      {/* Suspense for lazy-loaded pages - null fallback for instant transition */}
      <Suspense fallback={null}>
      <Routes>
        {/* HomePage and WaitlistPage handled outside Layout to control navigation visibility */}
        <Route path="/" element={<HomePage />} />
        <Route path="waitlist" element={<WaitlistPage />} />
        <Route path="privacy" element={<PrivacyPage />} />
        <Route path="terms" element={<TermsPage />} />
        <Route path="help/sms" element={<SmsHelpPage />} />
        <Route path="help/sign-up" element={<HelpArticlePage slug="sign-up" />} />
        <Route path="getting-started" element={<LeaderGettingStartedPage />} />
        <Route path="leaders" element={<LeaderWhyPage />} />
        <Route path="members" element={<MembersPage />} />
        {/* #3126: the App Store landing page on oro.fit. NOTE: backend
            `index.ts` also mounts `/app` (`app.route('/app', appMeta)`), but on
            api.oro.fit — a different host — so the two never collide. `/ios`
            stays the _redirects 302 to the store (badges + FAQ link there). */}
        <Route path="app" element={<AppPage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="faq" element={<FaqPage />} />
        <Route path="contact" element={<ContactPage />} />
        <Route path="unsubscribed" element={<UnsubscribedPage />} />
        <Route path="unsubscribe/error" element={<UnsubscribeErrorPage />} />
        <Route path="invite/:code" element={<InviteAcceptPage />} />

        <Route path="/" element={<Layout />}>
          {/* Public routes */}
          <Route path="login" element={<LoginRedirect />} />
          <Route path="magic-login" element={<MagicLoginPage />} />
          {/*
            #3167: browser-origin magic links land here instead of /magic-login.
            /signin is deliberately NOT in APP_LINK_PATHS (backend
            utils/wellKnown.ts), so iOS/Android never claim it — a link the user
            requested in the browser stays in the browser. Renders the SAME
            MagicLoginPage, which reads the token from the query string, so the
            web session is minted via cookie exactly as /magic-login does.
          */}
          <Route path="signin" element={<MagicLoginPage />} />
        <Route path="admin/invites" element={<ProtectedRoute><AdminInvitesPage /></ProtectedRoute>} />
        <Route path="admin/reports" element={<ProtectedRoute><AdminReportsPage /></ProtectedRoute>} />
        <Route path="admin/feedback" element={<ProtectedRoute><AdminFeedbackPage /></ProtectedRoute>} />
        <Route path="admin/contact-messages" element={<ProtectedRoute><AdminContactMessagesPage /></ProtectedRoute>} />
        <Route path="platform-admin/home" element={<ProtectedRoute><PlatformAdminHomePage /></ProtectedRoute>} />
        <Route path="platform-admin/clubs" element={<ProtectedRoute><PlatformAdminClubsPage /></ProtectedRoute>} />
        <Route path="platform-admin/clubs/:slug" element={<ProtectedRoute><PlatformAdminClubDetailPage /></ProtectedRoute>} />
        <Route path="platform-admin/members" element={<ProtectedRoute><PlatformAdminMembersPage /></ProtectedRoute>} />
        <Route path="platform-admin/subscriptions" element={<ProtectedRoute><PlatformAdminSubscriptionsPage /></ProtectedRoute>} />
        {/* OnboardingPage route removed (#691) - OnboardingModal handles all onboarding */}
        {/* Club-first navigation: /home redirects to primary club (Issue #695) */}
        <Route path="home" element={<ProtectedRoute><HomeRedirect /></ProtectedRoute>} />
        {/* AppHomePage moved to /overview (no longer in bottom nav) */}
        <Route path="overview" element={<ProtectedRoute><AppHomePage /></ProtectedRoute>} />
        <Route path="clubs" element={<ProtectedRoute><ClubsPage /></ProtectedRoute>} />
        <Route path="activity/*" element={<ProtectedRoute><ActivityPage /></ProtectedRoute>} />
        {/* Legacy redirect: /events → /activity/events */}
        <Route path="events" element={<Navigate to="/activity/events" replace />} />
        <Route path="events/create" element={<ProtectedRoute><GlobalEventCreatePage /></ProtectedRoute>} />
        {/* Training Group routes hidden for v1.2 release (#1241) - re-enable in v1.2.1 */}
        {/* <Route path="training-groups/create" element={<ProtectedRoute><TrainingGroupCreatePage /></ProtectedRoute>} /> */}
        {/* <Route path="training-groups/:id" element={<ProtectedRoute><TrainingGroupDetailPage /></ProtectedRoute>} /> */}
        {/* <Route path="training-groups/:id/edit" element={<ProtectedRoute><TrainingGroupEditPage /></ProtectedRoute>} /> */}
        {/* <Route path="training-groups/:id/events/new" element={<ProtectedRoute><TrainingGroupEventCreatePage /></ProtectedRoute>} /> */}
        <Route path="training-groups/*" element={<Navigate to="/activity/events" replace />} />
        <Route path="me" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        {/* Legacy profile edit - redirect to new settings */}
        <Route path="me/edit" element={<Navigate to="/settings/profile" replace />} />
        {/* Consolidated to a single notifications surface (#2652) - redirect legacy path.
            Shared element (routes/legacyRedirects) so the redirect regression test
            consumes the shipped route, not a decoupled copy. */}
        {meNotificationsRedirectRoute}
        {/* New Settings Hub & Sub-Pages */}
        <Route path="settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="settings/profile" element={<ProtectedRoute><ProfileSettingsPage /></ProtectedRoute>} />
        <Route path="settings/email-change" element={<ProtectedRoute><EmailChangePage /></ProtectedRoute>} />
        <Route path="settings/email-change/confirm" element={<ProtectedRoute><EmailChangeConfirmPage /></ProtectedRoute>} />
        <Route path="settings/account" element={<ProtectedRoute><AccountManagementPage /></ProtectedRoute>} />
        {/* #3229: NOT ProtectedRoute. The emailed magic-link token is the
            credential (single-use, 15-min, purpose-scoped, user-scoped), so the
            page must render for a logged-out browser — the reviewer's App Store
            5.1.1(v) dead-end was landing here with no session and being bounced
            to login. The backend derives the user from the token, not a session. */}
        <Route path="settings/delete-account/confirm" element={<DeleteAccountConfirmPage />} />
        <Route path="settings/preferences" element={<ProtectedRoute><PreferencesSettingsPage /></ProtectedRoute>} />
        <Route path="settings/accessibility" element={<ProtectedRoute><AccessibilitySettingsPage /></ProtectedRoute>} />
        <Route path="settings/notifications" element={<ProtectedRoute><NotificationSettingsPage /></ProtectedRoute>} />
        <Route path="settings/calendar" element={<ProtectedRoute><CalendarFeedPage /></ProtectedRoute>} />
        <Route path="settings/blocked" element={<ProtectedRoute><BlockedUsersPage /></ProtectedRoute>} />
        <Route path="release-notes" element={<ProtectedRoute><ReleaseNotesPage /></ProtectedRoute>} />
        <Route path="feedback" element={<FeedbackPage />} />
        {/* Help Center (Issue #1348). Index + category pages stay gated; the
            article route is public at the router level as of #2836 (see below). */}
        <Route path="help" element={<ProtectedRoute><HelpIndexPage /></ProtectedRoute>} />
        <Route path="help/:category" element={<ProtectedRoute><HelpCategoryPage /></ProtectedRoute>} />
        {/* #2836: gate moved from route to article — HelpArticlePage enforces
            per-article auth (fail-closed: only isPublic articles render for
            guests). The hardcoded /help/sign-up route above stays a public alias. */}
        <Route path="help/:category/:slug" element={<HelpArticlePage />} />
        {/* Old SettingsPage temporarily kept for reference - will be deleted after Pass 3 */}
        {/* <Route path="settings-old" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} /> */}
        <Route path="u/:id" element={<PublicProfilePage />} />
        {/* Legacy `/member/:id` retired (#3348): the protected MemberDetailPage
            fetched GET /members/:id, an endpoint no router mounts, so it could only
            render an error. Forward to the live public profile, id preserved.
            Shared element (routes/legacyRedirects) so the redirect regression test
            consumes the shipped route, not a decoupled copy. */}
        {memberIdRedirectRoute}
        {/* #2846: dev-only route — registered only when UiPlayground is non-null
            (dev/qa builds + dev server). In a production build UiPlayground folds
            to null, so this Route is not registered and /playground 404s. */}
        {UiPlayground && (
          <Route path="playground" element={<UiPlayground />} />
        )}
        
        {/* Protected routes - require authentication */}
        <Route path="clubs/create" element={<ProtectedRoute><CreateClubPage /></ProtectedRoute>} />
        <Route path="clubs/:slug" element={<ProtectedRoute><ClubDetailPage /></ProtectedRoute>} />
        <Route path="clubs/:slug/members" element={<ProtectedRoute><ClubMembersPage /></ProtectedRoute>} />
        <Route path="clubs/:slug/settings" element={<ProtectedRoute><ClubSettingsHubPage /></ProtectedRoute>} />
        <Route path="clubs/:slug/settings/general" element={<ProtectedRoute><ClubSettingsGeneralPage /></ProtectedRoute>} />
        <Route path="clubs/:slug/settings/privacy" element={<ProtectedRoute><ClubSettingsPrivacyPage /></ProtectedRoute>} />
        {/* Legacy settings/members - redirect to unified members page (#1227) */}
        <Route path="clubs/:slug/settings/members" element={<ProtectedRoute><ClubSettingsMembersRedirect /></ProtectedRoute>} />
        <Route path="clubs/:slug/settings/invites" element={<ProtectedRoute><ClubSettingsInvitesPage /></ProtectedRoute>} />
        <Route path="clubs/:slug/settings/danger" element={<ProtectedRoute><ClubSettingsDangerPage /></ProtectedRoute>} />
        <Route path="clubs/:slug/settings/calendar" element={<ProtectedRoute><ClubSettingsCalendarPage /></ProtectedRoute>} />
        <Route path="clubs/:slug/settings/events" element={<ProtectedRoute><ClubSettingsEventsPage /></ProtectedRoute>} />
        <Route path="clubs/:slug/settings/requests" element={<ProtectedRoute><ClubSettingsMembershipRequestsPage /></ProtectedRoute>} />
        {/* Legacy club edit - redirect with wrapper for dynamic slug */}
        <Route path="clubs/:slug/edit" element={<ProtectedRoute><ClubEditRedirect /></ProtectedRoute>} />
        <Route path="clubs/:slug/chat" element={<ProtectedRoute><ClubChatPage /></ProtectedRoute>} />
        {/* Chat landing page for users without club context (#809) */}
        <Route path="chat" element={<ProtectedRoute><ChatLandingPage /></ProtectedRoute>} />
        <Route path="clubs/:slug/events" element={<ProtectedRoute><ClubEventsPage /></ProtectedRoute>} />
        <Route path="clubs/:slug/events/manage" element={<ProtectedRoute><ClubEventsManagePage /></ProtectedRoute>} />
        <Route path="clubs/:slug/events/create" element={<ProtectedRoute><EventCreatePage /></ProtectedRoute>} />
        {/* Event Series Routes (#1208) */}
        <Route path="clubs/:slug/series/:seriesId" element={<ProtectedRoute><EventSeriesDetailPage /></ProtectedRoute>} />
        <Route path="clubs/:slug/series/:seriesId/edit" element={<ProtectedRoute><EventSeriesEditPage /></ProtectedRoute>} />
        <Route path="event/:id" element={<EventDetailPage />} />
        <Route path="event/:id/check-in" element={<ProtectedRoute><EventCheckInPage /></ProtectedRoute>} />
        <Route path="event/:id/admin-checkin" element={<ProtectedRoute><EventAdminCheckInPage /></ProtectedRoute>} />
        <Route path="event/:id/edit" element={<ProtectedRoute><EventEditPage /></ProtectedRoute>} />
        <Route path="scan-qr" element={<ProtectedRoute><QRScannerPage /></ProtectedRoute>} />
        <Route path="loading" element={<LoadingPage />} />
        {/* More routes will be added as we build features */}
        
        {/* Catch-all route for 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      </Routes>
      </Suspense>
      
      <LoginModal
        isOpen={isModalOpen}
        onClose={closeLoginModal}
        pendingNavigation={pendingNavigation}
      />

      {/* Onboarding Modal (Issue #563) */}
      {shouldShowOnboarding && (
        <OnboardingModal
          user={user}
          context={user.onboarding_context}
        />
      )}

      {/* Tour Prompt (#1559) — only shown when not actively running the tour */}
      {shouldShowTourPrompt && <TourPromptGate />}
    </TourProvider>
  )
}

/**
 * Hides the prompt while the tour is running so the modal does not
 * overlap the joyride spotlight. Persisted state hides it permanently.
 */
function TourPromptGate() {
  const { isActive } = useTour();
  if (isActive) return null;
  return <TourPrompt />;
}

function App() {
  return (
    <AuthModalProvider>
      <AppContent />
      <Toaster 
        position="top-center"
        visibleToasts={3}
        richColors
        closeButton
        toastOptions={{
          duration: 3000,
          classNames: {
            // #2258: only REAL Layer-2 tokens — an unresolved var() fails
            // silently (#1568 shape). Text tokens are --text-*, and the
            // accent family is gold (--oro-gold / --oro-gold-light) with
            // black text, per Button's `oro` variant.
            toast: 'bg-[var(--oro-surface)] border-[var(--oro-border)] text-[var(--text-primary)]',
            title: 'text-[var(--text-primary)] font-medium',
            description: 'text-[var(--text-secondary)]',
            actionButton: 'bg-[var(--oro-gold)] text-black hover:bg-[var(--oro-gold-light)]',
            closeButton: 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
          },
        }}
      />
    </AuthModalProvider>
  )
}

export default App
